import { formatISODate } from 'components/formatter.utils';
import dayjs, { type Dayjs } from 'dayjs';
import isNil from 'lodash/fp/isNil';

export const canBeSelected = (
  minDate: Dayjs | null | undefined,
  maxDate: Dayjs | null | undefined,
  dateRange: [Dayjs, Dayjs] | null | undefined
): boolean => {
  if (isNil(dateRange)) {
    return true;
  }

  if (!isNil(minDate) && minDate.isAfter(dateRange[0])) {
    return false;
  }

  if (!isNil(maxDate) && maxDate.isBefore(dateRange[0])) {
    return false;
  }

  return true;
};

export const ensureDateRangeWithinBounds = <AllowOnlySince extends boolean = false>(
  minDate: Dayjs | null | undefined,
  maxDate: Dayjs | null | undefined,
  range: [Dayjs, Dayjs | null] | null,
  allowSelectingOnlySince?: AllowOnlySince
): [Dayjs, AllowOnlySince extends true ? Dayjs | null : Dayjs] | null => {
  if (range === null) {
    return null;
  }

  const [start, end] = range;
  const startOrMinDate = isNil(minDate) ? start : (dayjs.max(minDate, start) as Dayjs);
  if (allowSelectingOnlySince && isNil(end)) {
    return [startOrMinDate, null as AllowOnlySince extends true ? Dayjs | null : Dayjs];
  }

  if (isNil(end)) {
    throw new Error('End date is required');
  }

  const endOrMaxDate = isNil(maxDate) ? end : (dayjs.min(maxDate, end) as Dayjs);
  return [startOrMinDate, endOrMaxDate];
};

export const convertDateRangeToSinceToDate = (
  dateRange: [Dayjs, Dayjs] | null
): {
  since: UtcDate | null;
  to: UtcDate | null;
} => {
  return {
    since: isNil(dateRange) ? null : formatISODate(dateRange[0]),
    to: isNil(dateRange) ? null : formatISODate(dateRange[1]),
  };
};

export const convertDateRangeToSinceToDateTime = (
  dateRange: [Dayjs, Dayjs] | null
): {
  since: string | null;
  to: string | null;
} => {
  return {
    since: isNil(dateRange) ? null : dateRange[0].toISOString(),
    to: isNil(dateRange) ? null : dateRange[1].toISOString(),
  };
};

const isValidDayjsDateRangeFactory = (
  allowOnlySince: boolean
): ((dateRange: unknown) => dateRange is null | [Dayjs, Dayjs]) => {
  return (dateRange: unknown): dateRange is null | [Dayjs, Dayjs] => {
    if (isNil(dateRange)) {
      return true;
    }

    if (!Array.isArray(dateRange)) {
      return false;
    }

    if (dateRange.length !== 2) {
      return false;
    }

    if (isNil(dateRange[1]) && allowOnlySince) {
      return dayjs.isDayjs(dateRange[0]) && dateRange[0].isValid();
    }

    if (dateRange.some((date) => !dayjs.isDayjs(date))) {
      return false;
    }

    if (!dateRange.every((date) => date.isValid())) {
      return false;
    }

    return !dateRange[0].isAfter(dateRange[1]);
  };
};

export const isValidDayjsDateRange = isValidDayjsDateRangeFactory(false);
export const isValidDayjsDateRangeWithOnlySince = isValidDayjsDateRangeFactory(true);
